/*
 * SIGGO — Red de seguridad del estado sticky del header.
 *
 * EL PROBLEMA
 * Todo el aspecto del header al bajar (fondo blanco, texto e iconos negros,
 * logo normal en vez del inverso, color del globo del carrito) cuelga de UN
 * solo atributo, `data-sticky-state`, que Horizon escribe desde un unico
 * IntersectionObserver creado una sola vez en `connectedCallback`:
 *
 *   - `threshold: 1`, asi que SOLO notifica cuando la razon de interseccion
 *     CRUZA el 1.0 exacto;
 *   - `root` elegido en ese instante segun el ancho (`.page-wrapper` a partir
 *     de 990px, el viewport por debajo), y nunca se vuelve a calcular;
 *   - `if (this.#intersectionObserver) return;`, o sea que no se recrea.
 *
 * Si esa notificacion se pierde una vez —porque el ancho cambia de lado del
 * breakpoint, porque el reparto llega con el layout a medio asentar, o porque
 * la razon nunca llega a valer exactamente 1 con medidas fraccionarias— el
 * atributo se queda con el valor viejo y **no hay nada que lo recalcule**:
 * el velo se queda a `height: 0`, el header transparente sobre el contenido y
 * el menu ilegible. Comprobado: forzando el atributo a `inactive` estando
 * abajo, seguir haciendo scroll NO lo arregla; solo se recupera subiendo del
 * todo y volviendo a bajar, que es justo lo que describia el cliente.
 *
 * LA SOLUCION
 * No tocar el observador —sigue siendo quien manda en el caso normal— y
 * añadir una comprobacion barata que deriva el estado de la GEOMETRIA REAL en
 * cada fotograma de scroll. La geometria no puede quedarse obsoleta: si el
 * header esta pegado arriba, `top` vale ~-1; si no, es mayor. Asi el atributo
 * converge siempre, venga de donde venga el fallo, sin depender de que ningun
 * evento llegue a tiempo.
 *
 * Se limita al modo `sticky="always"`, que es el que usa SIGGO. En
 * `scroll-up` el atributo tiene un tercer valor (`idle`) con su propia maquina
 * de estados, y ahi este guardian no debe opinar.
 */
(function () {
  const SELECTOR = '#header-component';

  /* El elemento pegajoso es `.header-section`, anclado con `top: -1px` para que
     el observador del tema pueda distinguir el estado. Cuando esta pegado su
     borde superior queda en -1; en reposo, por debajo de la franja de anuncios
     (34px). Medio pixel de margen absorbe el redondeo de los DPR fraccionarios
     (125% y 150% de escalado en Windows dan rects con decimales). */
  const STUCK_THRESHOLD = 0.5;

  let frame = null;

  function headers() {
    return document.querySelectorAll(SELECTOR);
  }

  function sync() {
    frame = null;

    for (const header of headers()) {
      if (header.getAttribute('sticky') !== 'always') continue;

      const top = header.getBoundingClientRect().top;
      const expected = top <= STUCK_THRESHOLD ? 'active' : 'inactive';

      // Solo se escribe cuando difiere: asi no se disparan transiciones ni
      // recalculos de estilo en cada fotograma de scroll.
      if (header.dataset.stickyState !== expected) {
        header.dataset.stickyState = expected;
      }
    }
  }

  function schedule() {
    if (frame !== null) return;
    frame = requestAnimationFrame(sync);
  }

  /* Los eventos `scroll` no burbujean, y en Horizon el contenedor que hace
     scroll cambia con el ancho: `.page-wrapper` en escritorio y el documento
     en movil. Escuchando en captura sobre `window` se reciben los de
     cualquiera de los dos sin tener que averiguar cual es, y sin volver a
     engancharse al redimensionar. */
  addEventListener('scroll', schedule, { capture: true, passive: true });
  addEventListener('resize', schedule, { passive: true });
  addEventListener('orientationchange', schedule, { passive: true });
  addEventListener('pageshow', schedule);

  // Primer ajuste en cuanto hay layout, para el caso de recargar con la pagina
  // ya desplazada (el navegador restaura el scroll y el observador puede no
  // llegar a notificar nada).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
  addEventListener('load', schedule, { once: true });
})();
